import React from "react";
import { IoCheckmarkCircle } from "react-icons/io5";
import { COUNTRIES, NO_COUNTRY, areaCountry } from "../../config/countries";
import { geoTypeLabel } from "../../config/products";
import AreaThumbnail from "./AreaThumbnail";

const CheckIcon = IoCheckmarkCircle as any;

/** What the card needs to know about an area. */
export interface CardArea {
  id: string;
  name: string;
  description?: string;
  type: string;
  ids?: string[];
  geometry?: string;
  country_code?: string | null;
}

export interface AreaCardProps {
  area: CardArea;
  /** Shows the selected state: a brand ring and a check mark on the thumbnail. */
  selected?: boolean;
  /** Dims the card, shows the reason at the bottom, under the footer, and makes it unclickable. */
  disabled?: { reason: string };
  /** Makes the card a control. Space and Enter do the same as a click. */
  onClick?: () => void;
  /** Role of the control when `onClick` is given: a checkbox (default), a radio, or a plain button. */
  role?: "checkbox" | "radio" | "button";
  /** Next to the name (the draft-data badge). */
  badges?: React.ReactNode;
  /** The bottom of the card. Clicks inside it never reach `onClick`. */
  footer?: React.ReactNode;
  /** Top right, beside the name (edit, delete, share). Clicks inside it never reach `onClick`. */
  actions?: React.ReactNode;
  /** Level of the name heading. */
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  /**
   * A visible, labelled checkbox on the thumbnail ("Add to project"), for cards whose footer is full of controls of
   * its own. Only the checkbox ticks the card; use it instead of `onClick`.
   */
  checkbox?: { label: string; onChange: () => void };
}

/** The first few ids of an area (or "WKT Polygon" for a drawn one) as small badges. */
export const AreaIdBadges: React.FC<{ area: CardArea; muted?: boolean }> = ({ area, muted }) => {
  const pill = `inline-flex items-center rounded-full border border-paper-300 px-2 py-0.5 text-[10px] font-mono ${
    muted ? "text-ink-faint" : "text-ink-subtle"
  }`;
  return (
    <div className="flex flex-wrap gap-1">
      {area.ids ? (
        <>
          {area.ids.slice(0, 3).map((id) => (
            <span key={id} className={pill}>
              {id}
            </span>
          ))}
          {area.ids.length > 3 && (
            <span className="inline-flex items-center rounded-full bg-paper-200 px-2 py-0.5 text-[10px] font-mono text-ink-subtle">
              +{area.ids.length - 3} more
            </span>
          )}
        </>
      ) : area.geometry ? (
        <span className={pill}>WKT Polygon</span>
      ) : (
        <span className={pill}>No geometry</span>
      )}
    </div>
  );
};

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

/**
 * The one card for a saved area: the Library and every area picker draw it. Thumbnail on top (lazy, cached, see
 * AreaThumbnail), then the name, the area type, the country and the description. Everything a page adds goes in
 * `badges`, `actions` and `footer`.
 */
const AreaCard: React.FC<AreaCardProps> = ({
  area,
  selected = false,
  disabled,
  onClick,
  role = "checkbox",
  badges,
  footer,
  actions,
  headingLevel = 4,
  checkbox,
}) => {
  const country = areaCountry(area);
  const countryInfo = country ? COUNTRIES[country] : NO_COUNTRY;
  const clickable = onClick !== undefined && !disabled;
  const Heading = `h${headingLevel}` as "h4";

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!clickable) return;
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <article
      aria-label={area.name}
      data-testid="area-card"
      data-selected={selected ? "true" : undefined}
      onClick={clickable ? onClick : undefined}
      className={`group relative overflow-hidden rounded-2xl border bg-surface p-4 shadow-soft transition-all duration-200 ${
        selected ? "border-brand-500 ring-2 ring-brand-500" : "border-paper-300/70"
      } ${disabled ? "opacity-60" : "hover:-translate-y-0.5 hover:shadow-card hover:border-brand-500/30"} ${
        clickable ? "cursor-pointer" : ""
      }`}
    >
      {onClick !== undefined && (
        // The control sits behind the content, so the actions and the footer can hold buttons of their own.
        <div
          role={role}
          tabIndex={disabled ? -1 : 0}
          aria-label={role === "button" ? area.name : `Select ${area.name}`}
          aria-checked={role === "button" ? undefined : selected}
          aria-disabled={disabled ? true : undefined}
          onKeyDown={onKeyDown}
          className="absolute inset-0 rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
        />
      )}
      <div className="relative -mx-4 -mt-4 mb-3">
        <AreaThumbnail areas={[area]} label={`Outline of ${area.name}`} />
        {checkbox && (
          <label
            onClick={stop}
            className={`absolute left-2 top-2 inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold shadow-soft ${
              selected ? "border-brand-500 bg-surface text-brand-700" : "border-paper-300 bg-surface text-ink hover:border-brand-500"
            }`}
          >
            <input
              type="checkbox"
              className="checkbox checkbox-xs checkbox-primary"
              checked={selected}
              disabled={!!disabled && !selected}
              onChange={checkbox.onChange}
              aria-label={`Select ${area.name}`}
            />
            <span aria-hidden="true">{checkbox.label}</span>
          </label>
        )}
        {selected && (
          <span
            data-testid="area-card-check"
            className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-surface text-brand-600 shadow-soft"
          >
            <CheckIcon aria-hidden="true" className="h-6 w-6" />
          </span>
        )}
      </div>
      <div className="relative flex items-start justify-between gap-2 mb-1">
        <Heading className={`font-display text-base font-semibold truncate ${disabled ? "text-ink-muted" : "text-ink"}`}>
          {area.name}
          {badges ? <span className="ml-2 align-middle">{badges}</span> : null}
        </Heading>
        {actions ? (
          <div className="flex gap-1 flex-shrink-0" onClick={stop}>
            {actions}
          </div>
        ) : null}
      </div>
      <div className="relative mb-2 flex flex-wrap items-center gap-2 text-xs text-ink-faint">
        <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 font-medium text-brand-700">
          {geoTypeLabel(area.type, country)}
        </span>
        <span>
          {countryInfo.flag} {countryInfo.label}
        </span>
      </div>
      {area.description ? (
        <p className="relative text-sm text-ink-muted mb-3 line-clamp-2">{area.description}</p>
      ) : null}
      {footer ? (
        <div className="relative" onClick={stop}>
          {footer}
        </div>
      ) : null}
      {disabled ? <p className="relative text-xs text-warn-ink">{disabled.reason}</p> : null}
    </article>
  );
};

export default AreaCard;
